import { defineStore, storeToRefs } from "pinia";
import { computed, ref } from "vue";

import MiscService from "../services/misc";
import { useUserStore } from "./user";
import { useI18n } from "vue-i18n";

export const useMiscStore = defineStore("misc", () => {
  const { t } = useI18n();

  const us = useUserStore();
  const { token } = storeToRefs(us);

  const softwareList = ref([]);
  const allSoftwareList = ref([]);
  const campaignList = ref([]);
  const hardwareList = ref([]);
  const localHardwareList = ref([]);
  const softwareVersionList = ref([]);
  const modulesList = ref([]);
  const softwarePrice = ref(0);
  const fidelzationPrice = ref(0);
  const countryList = ref([]);
  const fidelizationList = ref([]);
  const licenseHistory = ref([]);

  // New refs for admin API data
  const admSoftwareList = ref([]);
  const admCampaignList = ref([]);
  const admHardwareList = ref([]);

  const ms = new MiscService();

  /**
   * Sorts software versions alphabetically by name.
   * Uses natural (numeric) comparison so "Versão 2" comes before "Versão 10".
   * @param {Array} versions - Versions list
   * @returns {Array} Sorted versions list
   */
  function sortVersionsByName(versions) {
    return [...versions].sort((a, b) =>
      String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );
  }

  // ZSLW-64 - Software version name
  const softwareVersionName = computed(() => {
    return (software_id, type) => {
      const soft = allSoftwareList.value.find((e) => e.codigo == software_id);
      if (soft) {
        const version = soft.versions.find((v) => v.id == type);
        if (version) {
          return version.name;
        } else {
          return "";
        }
      } else {
        return "";
      }
    };
  });

  async function getSoftwareList() {
    if (softwareList.value.length > 0) {
      return;
    }
    try {
      const data = await ms.getSoftwareList(token.value);

      softwareList.value = [
        // { descricao: "Escolha o software", codigo: -1 },
        ...data.data,
      ];
    } catch (error) {
      softwareList.value = [];
    }
  }

  async function getAllSoftwareList(country_id = "PT") {
    if (softwareList.value.length > 0) {
      return;
    }
    try {
      const data = await ms.getAllSoftwareList(token.value, country_id);

      allSoftwareList.value = data.data;
    } catch (error) {
      softwareList.value = [];
    }
  }

  async function getCampaignList() {
    if (campaignList.value.length > 0) {
      return;
    }
    try {
      const data = await ms.getCampaignList(token.value);
      const noneStr = t("general.none_female");
      campaignList.value = [{ descricao: noneStr, id: -1 }, ...data.data];
      campaignList.value = campaignList.value.map((item) => {
        return {
          ...item,
          descricao: item.descricao.replace("?", "€"),
        };
      });
    } catch (error) {
      console.log("getCampaignList -> error", error);
      campaignList.value = [];
    }
  }

  async function getHardwareList(
    software_id,
    software_version,
    country_id,
    pos = -1
  ) {
    try {
      const data = await ms.getHardwareList(
        token.value,
        software_id,
        software_version,
        country_id,
        pos
      );
      let list = null;
      if (Array.isArray(data.data) && data.data.length > 0) {
        list = data.data.map((item) => {
          return {
            ...item,
            descricao:
              item.descricao + " - " + parseFloat(item.valor).toFixed(2) + "€",
          };
        });
      } else list = data.data;

      if (pos > -1) {
        hardwareList.value = [list];
      } else {
        hardwareList.value = [
          { descricao: t("general.none_male"), hardware: -1 },
          ...list,
        ];
      }
      return hardwareList.value;
    } catch (error) {
      console.log("getHardwareList -> error", error);
      hardwareList.value = [];
    }
  }

  async function getSoftwareVersionList(software_id, type, country_id = "PT") {
    try {
      const data = await ms.getSoftwareVersionList(
        token.value,
        software_id,
        country_id,
        type
      );
      softwareVersionList.value = sortVersionsByName(
        (data?.data || []).map((item) => {
          return {
            ...item,
            name: item.name,
          };
        })
      );

      return softwareVersionList.value;
    } catch (error) {
      console.log("getSoftwareVersionList -> error", error);
      softwareVersionList.value = [];
    }
  }

  async function getSoftwarePrice(software_id, software_version) {
    try {
      const data = await ms.getSoftwarePrice(
        token.value,
        software_id,
        software_version
      );
      softwarePrice.value = data.data;
      return data.data;
    } catch (error) {
      console.log("getSoftwarePrice -> error", error);
      softwarePrice.value = 0;
    }
  }

  async function getFidelizationPrice(
    software_id,
    software_version,
    country_id
  ) {
    try {
      const data = await ms.getFidelizationPrice(
        token.value,
        software_id,
        software_version,
        country_id
      );
      fidelzationPrice.value = data.data.price;
    } catch (error) {
      console.log("getFidelizationPrice -> error", error);
      fidelzationPrice.value = 0;
    }
  }

  async function getModulesList(software_id) {
    try {
      const data = await ms.getModulesList(token.value, software_id);
      modulesList.value = data.data;
    } catch (error) {
      console.log("getModulesList -> error", error);
      modulesList.value = [];
    }
  }

  async function getCountriesList() {
    try {
      const data = await ms.getCountriesList(token.value);
      countryList.value = data.data;
    } catch (error) {
      console.log("getCountriesList -> error", error);
      countryList.value = [];
    }
  }

  async function getFidelizationList() {
    try {
      const data = await ms.getFidelizationList(token.value);

      let list = data?.data ?? [];

      fidelizationList.value =
        list.map((e) => {
          if (e.id == 1) return { ...e, name: "Freebee Base" };
          if (e.id == 2) return { ...e, name: "Freebee Plus" };
          if (e.id == 3) return { ...e, name: "Freebee Plus+" };
        }) || [];

      if (fidelizationList.value.length > 0) {
        fidelizationList.value = [
          { name: t("general.none_female"), id: -1 },
          ...fidelizationList.value,
        ];
      }
    } catch (error) {
      console.log("getFidelizationList -> error", error);
      fidelizationList.value = [];
    }
  }

  const cloudSoftware = computed(
    
    () => softwareList.value?.filter((e) => (e?.type ?? "").includes("cloud")) ?? []
  );

  async function getLicenseHistory(license_id) {
    try {
      const data = await ms.getLicenseHistory(token.value, license_id);
      licenseHistory.value = (data?.data || []).map((item, index) => ({
        ...item,
        id: index, // Add unique ID for grid row expansion
      }));
      return licenseHistory.value;
    } catch (error) {
      console.log("getLicenseHistory -> error", error);
      licenseHistory.value = [];
    }
  }

  async function getLocalHardwareList() {
    try {
      const data = await ms.getLocalHardwareList(token.value);
      localHardwareList.value = data.data;
      return data.data;
    } catch (error) {
      console.log("getLocalHardwareList -> error", error);
      localHardwareList.value = [];
    }
  }

  // ===== NEW ADM API METHODS =====

  /**
   * Get software list from admin API with versions
   * Maps new API structure to existing format for backwards compatibility
   * @param {string} country - Country code
   * @param {boolean} forceRefresh - Force refresh even if data exists
   */
  async function getAdmSoftwareList(country = "PT", forceRefresh = false) {
    if (admSoftwareList.value.length > 0 && !forceRefresh) {
      return admSoftwareList.value;
    }
    try {
      const data = await ms.getAdmSoftwareList(token.value, country, true);
      
      // Store raw admin data
      admSoftwareList.value = data.data || [];
      
      // Map to existing softwareList format for backwards compatibility
      // Old format: { codigo, descricao, type }
      // New format: { id, description, active, mobile, certificated, versions }
      softwareList.value = (data.data || [])
        .filter(s => s.active)
        .map(software => {
          // Determine type from versions (cloud, local, web)
          const versionTypes = (software.versions || []).map(v => v.category);
          const type = versionTypes.includes('cloud') ? 'cloud' : 
                       versionTypes.includes('local') ? 'local' : 
                       versionTypes.includes('web') ? 'web' : '';
          
          return {
            codigo: software.id,
            descricao: software.description,
            type: type,
            mobile: software.mobile,
            certificated: software.certificated,
            versions: software.versions || []
          };
        });
      
      // Also update allSoftwareList for softwareVersionName computed
      allSoftwareList.value = softwareList.value.map(s => ({
        ...s,
        versions: s.versions.map(v => ({
          id: v.type,
          name: v.name,
          type: v.type,
          category: v.category,
          fidelizacao: v.fidelizacao ?? true
        }))
      }));
      
      return softwareList.value;
    } catch (error) {
      console.log("getAdmSoftwareList -> error", error);
      admSoftwareList.value = [];
      softwareList.value = [];
      return [];
    }
  }

  /**
   * Get software versions from admin API
   * Maps to existing softwareVersionList format
   * @param {number} softwareId - Software ID
   * @param {string} country - Country code
   */
  async function getAdmSoftwareVersions(softwareId, country = "PT") {
    try {
      // First check if we already have versions in admSoftwareList
      const software = admSoftwareList.value.find(s => s.id === softwareId);
      if (software && software.versions && software.versions.length > 0) {
        // Map versions to existing format
        // Old format: { type, name, fidelizacao }
        // New format: { id, adm_software_id, name, type, category, country, default_price, currency, active, station }
        softwareVersionList.value = sortVersionsByName(
          software.versions
            .filter(v => v.active && v.country === country)
            .map(version => ({
              type: version.type,
              name: version.name,
              fidelizacao: version.fidelizacao ?? true,
              category: version.category,
              id: version.id,
              default_price: version.default_price,
              currency: version.currency
            }))
        );
        return softwareVersionList.value;
      }
      
      // Fallback to API call
      const data = await ms.getAdmSoftwareVersions(token.value, softwareId, country);
      softwareVersionList.value = sortVersionsByName(
        (data.data || [])
          .filter(v => v.active)
          .map(version => ({
            type: version.type,
            name: version.name,
            fidelizacao: version.fidelizacao ?? true,
            category: version.category,
            id: version.id,
            default_price: version.default_price,
            currency: version.currency
          }))
      );

      return softwareVersionList.value;
    } catch (error) {
      console.log("getAdmSoftwareVersions -> error", error);
      softwareVersionList.value = [];
      return [];
    }
  }

  /**
   * Get campaigns from admin API
   * Maps to existing campaignList format
   * @param {string} country - Country code
   * @param {boolean} forceRefresh - Force refresh even if data exists
   */
  async function getAdmCampaignList(country = "PT", forceRefresh = false) {
    if (admCampaignList.value.length > 0 && !forceRefresh) {
      return campaignList.value;
    }
    try {
      const data = await ms.getAdmCampaignList(token.value, country);
      const noneStr = t("general.none_female");
      
      // Store raw admin data
      admCampaignList.value = data.data || [];
      
      // Map to existing campaignList format
      // Old format: { id, software, versao, data_inicio, data_fim, faturacao, hardware, preco_hardware, preco, freebee, fidelizacao, descricao }
      // New format (bundle): { id, name, description, bundle_type, items, ... }

      campaignList.value = [
        { name: noneStr, id: -1 },
        ...data.data
      ]

      // campaignList.value = [
      //   { descricao: noneStr, id: -1 },
      //   ...(data.data || []).map(campaign => {
      //     // Extract software and hardware from bundle items if available
      //     const softwareItem = (campaign.items || []).find(i => i.item_type === 'software');
      //     const hardwareItem = (campaign.items || []).find(i => i.item_type === 'hardware');
          
      //     return {
      //       id: campaign.id,
      //       descricao: (campaign.name || campaign.description || '').replace("?", "€"),
      //       software: softwareItem?.software_version_id ? 
      //         admSoftwareList.value.find(s => s.versions?.some(v => v.id === softwareItem.software_version_id))?.id || -1 : -1,
      //       versao: softwareItem?.software_version_id || -1,
      //       hardware: hardwareItem?.item_id || -1,
      //       preco: campaign.price || 0,
      //       preco_hardware: hardwareItem?.price || 0,
      //       faturacao: campaign.faturacao || 0,
      //       fidelizacao: campaign.fidelizacao || 999,
      //       freebee: campaign.freebee || 0,
      //       data_inicio: campaign.start,
      //       data_fim: campaign.end
      //     };
      //   })
      // ];
      
      return campaignList.value;
    } catch (error) {
      console.log("getAdmCampaignList -> error", error);
      admCampaignList.value = [];
      campaignList.value = [{ descricao: t("general.none_female"), id: -1 }];
      return campaignList.value;
    }
  }

  /**
   * Get hardware with prices from admin API
   * Maps to existing hardwareList format
   * @param {number} softwareVersionId - Software Version ID
   * @param {string} country - Country code
   */
  async function getAdmHardwareWithPrices(softwareVersionId, country = "PT") {
    try {
      const data = await ms.getHardwareWithPrices(token.value, softwareVersionId, country);
      
      // Store raw admin data
      admHardwareList.value = data.data || [];
      
      // Map to existing hardwareList format
      // Old format: { hardware, descricao, valor }
      // New format: { id, name, default_price, default_currency, prices: [...] }
      const mappedList = (data.data || [])
        .filter(hw => hw.active)
        .map(hardware => {
          // Get the price for this software version and country
          const priceObj = (hardware.prices || []).find(
            p => p.software_version_id === softwareVersionId && p.country === country
          ) || hardware.prices?.[0];
          
          const price = priceObj?.price ?? hardware.default_price ?? 0;
          
          return {
            hardware: hardware.id,
            codigo: hardware.id,
            descricao: hardware.name + " - " + parseFloat(price).toFixed(2) + "€",
            valor: price,
            monthly_fee: priceObj?.monthly_fee || 0,
            zsfact_code: hardware.zsfact_code
          };
        });
      
      hardwareList.value = [
        { descricao: t("general.none_male"), hardware: -1 },
        ...mappedList
      ];
      
      return hardwareList.value;
    } catch (error) {
      console.log("getAdmHardwareWithPrices -> error", error);
      admHardwareList.value = [];
      hardwareList.value = [{ descricao: t("general.none_male"), hardware: -1 }];
      return hardwareList.value;
    }
  }

  /**
   * Clear all cached data (useful when country changes)
   */
  function clearAdmCache() {
    admSoftwareList.value = [];
    admCampaignList.value = [];
    admHardwareList.value = [];
    softwareList.value = [];
    campaignList.value = [];
    hardwareList.value = [];
    softwareVersionList.value = [];
  }

  return {
    softwareList,
    getSoftwareList,
    campaignList,
    getCampaignList,
    hardwareList,
    getHardwareList,
    localHardwareList,
    getLocalHardwareList,
    softwareVersionList,
    getSoftwareVersionList,
    softwarePrice,
    getSoftwarePrice,
    fidelzationPrice,
    getFidelizationPrice,
    modulesList,
    getModulesList,
    cloudSoftware,
    getCountriesList,
    countryList,
    getFidelizationList,
    fidelizationList,
    getLicenseHistory,
    licenseHistory,
    getAllSoftwareList,
    allSoftwareList,
    softwareVersionName,
    // New ADM API exports
    admSoftwareList,
    admCampaignList,
    admHardwareList,
    getAdmSoftwareList,
    getAdmSoftwareVersions,
    getAdmCampaignList,
    getAdmHardwareWithPrices,
    clearAdmCache,
  };
});
